// src/utils/api.js
import axios from 'axios';
import { getCurrentDashboardMode } from '../contexts/DashboardModeContext';

// Use local backend in development, production URL otherwise
const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:3000/api'
  : 'https://grip-it-backend.onrender.com/api';

console.log('Using API base URL:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// Inject `mode` query param on every admin request so the backend can
// scope the response to test or prod data based on the admin header toggle.
api.interceptors.request.use((config) => {
  const url = config.url || '';
  if (url.startsWith('/admin') || url.includes('/admin/')) {
    const mode = getCurrentDashboardMode();
    config.params = { ...(config.params || {}), mode };
  }
  return config;
});

// Silent refresh + retry + single-flight queue
let isRefreshing = false;
let pendingQueue = [];

const flushQueue = (token) => {
  pendingQueue.forEach((cb) => cb.resolve(token));
  pendingQueue = [];
};

const rejectQueue = (error) => {
  pendingQueue.forEach((cb) => cb.reject(error));
  pendingQueue = [];
};

const terminalFailure = () => {
  setAuthToken(null);
  localStorage.removeItem('user');
  const path = window.location.pathname;
  if (!path.startsWith('/sign-in') && !path.startsWith('/complete-profile')) {
    const returnTo = window.location.pathname + window.location.search;
    window.location.href = '/sign-in?returnTo=' + encodeURIComponent(returnTo);
  }
};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    const url = error.config?.url || '';

    // Refresh endpoint itself failed — terminal
    if (url.includes('/auth/refresh')) {
      terminalFailure();
      return Promise.reject(error);
    }

    // Other auth endpoints (login/otp bad-credentials) — just reject
    if (url.startsWith('/auth/')) {
      return Promise.reject(error);
    }

    // Loop guard
    if (error.config._retry) {
      terminalFailure();
      return Promise.reject(error);
    }

    error.config._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({
          resolve: (token) => {
            error.config.headers.Authorization = 'Bearer ' + token;
            resolve(api(error.config));
          },
          reject: () => reject(error),
        });
      });
    }

    isRefreshing = true;

    return api
      .post('/auth/refresh')
      .then((res) => {
        const newToken = res.data.token;
        setAuthToken(newToken);
        flushQueue(newToken);
        error.config.headers.Authorization = 'Bearer ' + newToken;
        return api(error.config);
      })
      .catch((refreshError) => {
        rejectQueue(refreshError);
        terminalFailure();
        return Promise.reject(error);
      })
      .finally(() => {
        isRefreshing = false;
      });
  }
);

export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('token', token);
  } else {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');
  }
};

// Initialize auth token from localStorage
const token = localStorage.getItem('token');
if (token) {
  setAuthToken(token);
}

export const sendOtp = async (countryCode, mobileNumber) => {
  try {
    const response = await api.post('/auth/send-otp', { countryCode, mobileNumber });
    return response.data;
  } catch (error) {
    console.error('Error sending OTP:', error);
    if (error.response) {
      const msg = error.response.data.error || 'Could not send OTP. Please try again.';
      const err = new Error(msg);
      Object.assign(err, { error: msg, ...error.response.data });
      throw err;
    }
    throw new Error('Network error. Please check your connection.');
  }
};

export const verifyOtp = async (countryCode, mobileNumber, otp) => {
  try {
    const response = await api.post('/auth/verify-otp', { countryCode, mobileNumber, otp });
    setAuthToken(response.data.token);
    return response.data;
  } catch (error) {
    console.error('Error verifying OTP:', error);
    if (error.response) {
      const msg = error.response.data.error || 'Invalid OTP. Please try again.';
      const err = new Error(msg);
      Object.assign(err, { error: msg, ...error.response.data });
      throw err;
    }
    throw new Error('Network error. Please check your connection.');
  }
};

export const refreshToken = async () => {
  const res = await api.post('/auth/refresh');
  return res.data.token;
};

export const signOut = async () => {
  try {
    await api.post('/auth/logout');
  } catch (_) {
    // best-effort; clear client state regardless
  }
  setAuthToken(null);
  localStorage.removeItem('user');
};

export const googleSignIn = async (credential) => {
  try {
    const response = await api.post('/auth/google', { credential });
    setAuthToken(response.data.token);
    return response.data;
  } catch (error) {
    console.error('Error with Google sign-in:', error);
    if (error.response) {
      const msg = error.response.data.error || 'Google sign-in failed. Please try again.';
      const err = new Error(msg);
      Object.assign(err, { error: msg, ...error.response.data });
      throw err;
    }
    throw new Error('Network error. Please check your connection.');
  }
};

export const completeProfile = async (data) => {
  try {
    const response = await api.post('/auth/profile/complete', data);
    return response.data;
  } catch (error) {
    console.error('Error completing profile:', error);
    if (error.response) {
      const msg = error.response.data.error || 'An error occurred while completing profile';
      const err = new Error(msg);
      Object.assign(err, { error: msg, ...error.response.data });
      throw err;
    }
    throw new Error('Network error. Please check your connection.');
  }
};

export const updateProfile = async (data) => {
  try {
    const response = await api.put('/auth/profile', data);
    return response.data;
  } catch (error) {
    console.error('Error updating profile:', error);
    if (error.response) {
      const msg = error.response.data.error || 'An error occurred while updating profile';
      const err = new Error(msg);
      Object.assign(err, { error: msg, ...error.response.data });
      throw err;
    }
    throw new Error('Network error. Please check your connection.');
  }
};

export const getCourses = async () => {
  try {
    const response = await api.get('/courses');
    return response.data;
  } catch (error) {
    console.error('Error fetching courses:', error);
    throw error;
  }
};

export const getCourseById = async (courseId) => {
  try {
    const response = await api.get(`/courses/${courseId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching course:', error);
    throw error;
  }
};

export const getUserPurchasedCourses = async () => {
  try {
    const response = await api.get('/courses/user/purchased');
    return response.data;
  } catch (error) {
    console.error('Error fetching user\'s purchased courses:', error);
    throw error;
  }
};

export const createOrder = async (itemType, itemId, quantity) => {
  try {
    const response = await api.post('/payments/create-order', { itemType, itemId, quantity });
    return response.data;
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

export const verifyPayment = async (orderId) => {
  try {
    const response = await api.post('/payments/verify', { orderId });
    return response.data;
  } catch (error) {
    console.error('Error verifying payment:', error);
    throw error;
  }
};

export const subscribeToNewsletter = async (subscriptionData) => {
  try {
    const response = await api.post('/newsletter/subscribe', subscriptionData);
    return response.data;
  } catch (error) {
    console.error('Error subscribing to newsletter:', error);
    throw error.response ? error.response.data : error;
  }
};

export const unsubscribeFromNewsletter = async (email) => {
  try {
    const response = await api.post('/newsletter/unsubscribe', { email });
    return response.data;
  } catch (error) {
    console.error('Error unsubscribing from newsletter:', error);
    throw error.response ? error.response.data : error;
  }
};

export default api;

// Program APIs
export const getPrograms = async (tag) => {
  try {
    const params = tag ? { tag } : {};
    const response = await api.get('/programs', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching programs:', error);
    throw error;
  }
};

export const getProgramByDisplayId = async (displayId) => {
  try {
    const response = await api.get(`/programs/${displayId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching program:', error);
    throw error;
  }
};

export const getUserPurchasedPrograms = async () => {
  try {
    const response = await api.get('/programs/user/purchased');
    return response.data;
  } catch (error) {
    console.error('Error fetching purchased programs:', error);
    throw error;
  }
};

export const initiateProgramPurchase = async (programId, variantDisplayId, selectedTiming) => {
  try {
    const response = await api.post('/program-purchases/initiate', { programId, variantDisplayId, selectedTiming });
    return response.data;
  } catch (error) {
    console.error('Error initiating program purchase:', error);
    throw error.response ? error.response.data : error;
  }
};

export const verifyProgramPurchase = async (orderId) => {
  try {
    const response = await api.post('/program-purchases/verify', { orderId });
    return response.data;
  } catch (error) {
    console.error('Error verifying program purchase:', error);
    throw error;
  }
};

export const getPurchaseHistory = async () => {
  try {
    const response = await api.get('/payments/history');
    return response.data;
  } catch (error) {
    console.error('Error fetching purchase history:', error);
    throw error;
  }
};


export const getReceipt = async (orderId) => {
  try {
    const response = await api.get(`/payments/receipt/${orderId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching receipt:', error);
    throw error.response ? error.response.data : error;
  }
};
